import {
  hAmountOfSmsNeeded,
  isGsmMessage,
  getGsmMessageDoubleCharactersAmount,
  replaceAllPolishCharacters
} from "@/utils/helpers";
import { messageLimits } from "@/utils/consts";

// @vue/component
export default {
  name: "SmsGateway",

  components: {},

  props: {
    projectsList: {
      type: Array,
      default: () => []
    },
    userData: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      form: {
        project: "",
        number: null,
        smsContent: "",
        smsHeader: "",
        date: "",
        validatedSmsFromFile: [],
        template: ""
      },
      smsContentCount: 0,
      arePolishCharactersRemoved: false,
      fileContent: "",
      smsSendModel: "single",
      submitted: false,
      allSmsFromFile: [],
      isFileUploading: false,
      gridData: [
        {
          content: "123456789|Treść wiadomości"
        },
        {
          content: "123452334|Treść wiadomości"
        },
        {
          content: "234234234|Treść wiadomości"
        }
      ],
      rules: {
        project: [{ required: true, message: "Projekt jest wymagany.", trigger: "blur" }],
        number: [
          { required: true, message: "Numer jest wymagany.", trigger: "blur" },
          { validator: this.__validateNumber, trigger: "blur" }
        ],
        smsHeader: [{ required: true, message: "Nagłówek jest wymagany.", trigger: "blur" }],
        smsContent: [
          {
            required: true,
            message: "Treść wiadomości jest wymagana.",
            trigger: ["blur", "change"]
          },
          {
            min: 1,
            max: 918,
            message: "Długość wiadomości musi mieć min. 1 i max. 918 znaków.",
            trigger: "blur"
          }
        ],
        validatedSmsFromFile: [
          {
            type: "array",
            required: true,
            message: "Plik z numerami i treścią jest wymagany.",
            trigger: "blur"
          },
          { validator: this.__checkRecordsCount, trigger: "blur" }
        ]
      }
    };
  },

  watch: {
    fileContent: {
      handler: "readFile",
      immediate: false
    },
    smsSendModel: {
      handler: "clearForm"
    },
    projectsList: {
      handler(list) {
        if (list.length === 1) {
          this.form.project = list[0].uuid;
          this.selectProject(this.form.project);
        }
      },
      immediate: true
    },
    arePolishCharactersRemoved: {
      handler(val) {
        const specialCharacters = getGsmMessageDoubleCharactersAmount(this.form.smsContent);
        if (val) {
          this.smsContentCount = this.form.smsContent.length + specialCharacters;
        } else {
          const numberOfSpecialCharacters = this.smsContentHasSpecialCharacters
            ? 0
            : specialCharacters;
          this.smsContentCount = this.form.smsContent.length + numberOfSpecialCharacters;
        }
      }
    }
  },

  computed: {
    smsCounter() {
      return hAmountOfSmsNeeded(this.smsContentCount, this.smsContentHasSpecialCharacters);
    },
    smsTemplatesList() {
      return this.$store.getters["sms/templatesList"];
    },
    smsHeadersList() {
      return this.$store.getters["sms/headersList"];
    },
    isLoading() {
      return this.$store.getters["global/pendingAsyncStoreActions"].includes([
        "sms/addSmsToQueue",
        "sms/addMultipleSmsToQueue"
      ]);
    },
    isTemplatesLoading() {
      return this.$store.getters["global/pendingAsyncStoreActions"].includes([
        "sms/getSmsTemplatesList"
      ]);
    },
    isHeadersLoading() {
      return this.$store.getters["global/pendingAsyncStoreActions"].includes([
        "sms/getSmsHeadersList"
      ]);
    },
    smsContentLimit() {
      if (this.arePolishCharactersRemoved) return messageLimits.noSpecialCharacters;
      return this.smsContentHasSpecialCharacters
        ? messageLimits.withSpecialCharacters
        : messageLimits.noSpecialCharacters;
    },
    smsContentHasSpecialCharacters() {
      if (this.arePolishCharactersRemoved) return false;
      return !isGsmMessage(this.form.smsContent);
    }
  },

  methods: {
    handleSmsContentInput(value) {
      if (this.smsContentCount >= this.smsContentLimit) {
        if (value >= this.form.smsContent) {
          return;
        }
      }
      this.form.smsContent = value;
      const numberOfSpecialCharacters = this.smsContentHasSpecialCharacters
        ? 0
        : getGsmMessageDoubleCharactersAmount(value);
      this.smsContentCount = value.length + numberOfSpecialCharacters;
    },
    getSmsHeadersList() {
      this.$store.dispatch("sms/getSmsHeadersList");
    },
    selectProject(projectUuid) {
      this.$store.dispatch("sms/getSmsTemplatesList", projectUuid);
    },
    selectTemplate(templateContent) {
      this.form.smsContent = templateContent;
    },
    clearTemplate() {
      this.form.smsContent = "";
    },
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (this.smsContentCount > this.smsContentLimit) {
          this.$message({
            type: "error",
            message: "Długość wiadomości wykracza poza limit"
          });
          return;
        }
        if (valid) {
          if (this.smsSendModel === "single") {
            const parsedText = this.arePolishCharactersRemoved
              ? replaceAllPolishCharacters(this.form.smsContent)
              : this.form.smsContent;
            const params = {
              uuid_project: this.form.project,
              uuid_src: this.form.smsHeader,
              dst: this.form.number,
              text: parsedText,
              stamp: this.form.date
            };
            const responseStatus = await this.$store.dispatch("sms/addSmsToQueue", params);
            if (responseStatus === 200) {
              this.$message({
                type: "success",
                message: "Sms został dodany do kolejki."
              });
              await this.$store.dispatch("sms/getSmsQueueList");
              this.clearAfterSubmit();
            } else {
              this.$message({
                type: "error",
                message: "Wystąpił błąd podczas dodawania SMS'a do kolejki"
              });
            }
          } else {
            this.submitted = true;
            if (this.form.validatedSmsFromFile.length > 0) {
              this.isFileUploading = true;
              const params = {
                uuid_project: this.form.project,
                uuid_src: this.form.smsHeader,
                sms_from_file: this.form.validatedSmsFromFile,
                stamp: this.form.date
              };
              const responseStatus = await this.$store.dispatch(
                "sms/addMultipleSmsToQueue",
                params
              );
              if (responseStatus === 200) {
                this.$message({
                  type: "success",
                  message: "Sms'y zostały dodane do kolejki."
                });
                await this.$store.dispatch("sms/getSmsQueueList");
                this.clearAfterSubmit();
              } else {
                this.$message({
                  type: "error",
                  message: "Wystąpił błąd podczas dodawania SMS'ów do kolejki"
                });
              }
              this.isFileUploading = false;
            }
            this.submitted = false;
          }
        }
      });
    },
    readFile() {
      if (this.fileContent) {
        this.form.validatedSmsFromFile = [];
        this.allSmsFromFile = this.fileContent.split("\n");
        // eslint-disable-next-line no-restricted-globals
        this.allSmsFromFile.forEach(elem => {
          let singleSms = elem.split("|");
          let number = singleSms[0];
          let content = singleSms[1] ? singleSms[1].trim() : "";
          if (!isNaN(number) && content.length <= this.smsContentLimit && content.length > 0) {
            this.form.validatedSmsFromFile.push({
              number: number,
              smsContent: this.arePolishCharactersRemoved ? replaceAllPolishCharacters(content) : content
            });
            this.form.loadedFile = true;
          }
        });
      }
    },
    onFileChange(e) {
      let files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      this.createInput(files[0]);
    },
    createInput(file) {
      const reader = new FileReader();
      let vm = this;
      reader.onload = () => {
        vm.fileContent = reader.result;
      };
      reader.readAsText(file);
    },
    clearForm() {
      this.form.number = null;
      this.form.smsContent = "";
      this.form.smsHeader = "";
      this.form.project = "";
      this.form.date = "";
      this.form.validatedSmsFromFile = [];
      this.form.template = "";
      this.allSmsFromFile = [];
      this.form.validatedSmsFromFile = [];
      this.fileContent = "";
      this.smsContentCount = 0;
    },
    clearAfterSubmit() {
      this.form.number = null;
      this.form.smsContent = "";
      this.smsContentCount = 0;
      this.form.date = "";
      this.form.validatedSmsFromFile = [];
      this.form.template = "";
      this.allSmsFromFile = [];
      this.fileContent = "";
    },
    onlyNumber($event) {
      let keyCode = $event.keyCode ? $event.keyCode : $event.which;
      if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
        $event.preventDefault();
      }
    },
    __validateNumber(rule, value, callback) {
      if (value.length !== 9 && value.length !== 11) {
        callback(new Error("Numer telefonu musi mieć 9 lub 11 znaków."));
      } else {
        callback();
      }
    },
    __checkRecordsCount(rule, value, callback) {
      if (value.length > 1000) {
        callback(new Error("Maksymalnie 1000 rekordów."));
      } else {
        callback();
      }
    }
  },
  created() {
    this.getSmsHeadersList();
    if (this.smsHeadersList.length === 1) {
      this.form.smsHeader = this.smsHeadersList[0].user_name_uuid;
    }
  }
};
